import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEMANTIC, UI } from '../../styles/theme';
import KycProgressBar from '../KycProgressBar';
import BackButton from '../common/BackButton';
import API from '../../api';

// ── helpers ──────────────────────────────────────────────────────────────────
function detectBrand(num) {
  const n = num.replace(/\s/g, '');
  if (/^4/.test(n)) return 'visa';
  if (/^5[1-5]/.test(n) || /^2[2-7]/.test(n)) return 'mastercard';
  return null;
}

function fmtCard(val) {
  return val.replace(/\D/g, '').slice(0, 16).replace(/(.{4})(?=.)/g, '$1 ');
}

function fmtExpiry(val) {
  const d = val.replace(/\D/g, '').slice(0, 4);
  return d.length >= 3 ? d.slice(0, 2) + '/' + d.slice(2) : d;
}

function getExpiryError(val) {
  if (val.length !== 5) return '';
  const [mm, yy] = val.split('/');
  const month = parseInt(mm, 10);
  const year = 2000 + parseInt(yy, 10);
  if (month < 1 || month > 12) return 'Il mese deve essere compreso tra 01 e 12';
  const now = new Date();
  if (year < now.getFullYear() || (year === now.getFullYear() && month < now.getMonth() + 1)) {
    return 'Carta scaduta';
  }
  return '';
}

function maskCard(num) {
  const d = num.replace(/\s/g, '');
  if (d.length < 4) return num || '•••• •••• •••• ••••';
  const padded = d.padEnd(16, '•');
  return `${padded.slice(0, 4)} ${padded.slice(4, 8)} ${padded.slice(8, 12)} ${padded.slice(12, 16)}`;
}

// ── brand logos ──────────────────────────────────────────────────────────────
function VisaLogo({ size = 38 }) {
  return (
    <svg width={size} height={size * 0.32} viewBox="0 0 100 32" xmlns="http://www.w3.org/2000/svg">
      <text x="0" y="28" fontFamily="Arial Black,Arial,sans-serif" fontWeight="900"
        fontSize="36" fill="#1A1F71" letterSpacing="-2">VISA</text>
    </svg>
  );
}

function MastercardLogo({ size = 44 }) {
  const r = size * 0.42; // eslint-disable-line no-unused-vars
  return (
    <svg width={size} height={size * 0.65} viewBox="0 0 44 28" xmlns="http://www.w3.org/2000/svg">
      <circle cx="16" cy="14" r="13" fill="#EB001B"/>
      <circle cx="28" cy="14" r="13" fill="#F79E1B"/>
      <path d="M22 5.3a13 13 0 0 1 0 17.4A13 13 0 0 1 22 5.3z" fill="#FF5F00"/>
    </svg>
  );
}

// ── main component ────────────────────────────────────────────────────────────
const PaymentSetup = () => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { t }     = useTranslation();
  const userId    = localStorage.getItem('userId');

  // Plan info passed from MembershipSelection via navigate state, or fetched
  const [plan, setPlan] = useState(location.state?.plan || null);
  useEffect(() => {
    if (!plan) {
      API.get(`/users/${userId}`)
        .then(res => {
          const m = res.data?.membership || 'jupiter';
          API.get('/membership-tiers').then(r => {
            const found = (r.data || []).find(t => t.tier_key === m);
            if (found) setPlan({
              key: found.tier_key,
              name: found.display_name,
              color: found.color,
              img: found.icon_value,
              pricing: {
                value: `${(parseFloat(found.fee_percentage) * 100).toFixed(1)}%`,
                sub: found.subscription_monthly > 0 ? `+ $${found.subscription_monthly}/mese` : null,
              },
            });
          });
        });
    }
  }, [plan, userId]);

  // Form state
  const [name,   setName]   = useState('');
  const [card,   setCard]   = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv,    setCvv]    = useState('');
  const [cvvFocus,     setCvvFocus]     = useState(false);
  const [expiryError,  setExpiryError]  = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const cardRef   = useRef();
  const expiryRef = useRef();
  const cvvRef    = useRef();

  const brand = detectBrand(card);

  // Auto-advance focus
  const handleCardChange = (e) => {
    const val = fmtCard(e.target.value);
    setCard(val);
    if (val.replace(/\s/g, '').length === 16) expiryRef.current?.focus();
  };
  const handleExpiryChange = (e) => {
    const val = fmtExpiry(e.target.value);
    setExpiry(val);
    setExpiryError('');
    if (val.length === 5) {
      const err = getExpiryError(val);
      setExpiryError(err);
      if (!err) cvvRef.current?.focus();
    }
  };

  const handleExpiryBlur = () => {
    if (expiry.length > 0) setExpiryError(getExpiryError(expiry));
  };
  const handleCvvChange = (e) => {
    setCvv(e.target.value.replace(/\D/g, '').slice(0, 4));
  };

  const isValid = name.trim().length > 1
    && card.replace(/\s/g, '').length === 16
    && expiry.length === 5
    && !getExpiryError(expiry)
    && cvv.length >= 3
    && brand !== null;

  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setError('');
    try {
      const [month, year] = expiry.split('/');
      await API.post('/kyc/payment-method', {
        user_id:    userId,
        brand,
        last4:      card.replace(/\s/g, '').slice(-4),
        exp_month:  parseInt(month, 10),
        exp_year:   2000 + parseInt(year, 10),
        cardholder: name.trim(),
      });
      navigate('/kyc/privacy-consents');
    } catch {
      setError(t('kyc.payment.error_save', 'Errore nel salvataggio. Riprova.'));
    } finally {
      setSaving(false);
    }
  };

  const planColor = plan?.color || '#c0c0c0';
  const cardBg = 'linear-gradient(135deg, #1a1a3e 0%, #2d2d5e 60%, #1e1e4a 100%)';

  return (
    <div style={{ backgroundColor: SEMANTIC.backgroundApp, minHeight: '100vh', display: 'flex', flexDirection: 'column', padding: UI.spacing.lg }}>
      <div style={{ maxWidth: 480, margin: '0 auto', width: '100%' }}>

        {/* Back */}
        <BackButton onClick={() => navigate('/kyc/membership')} />

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: UI.spacing.lg }}>
          <img src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`} alt="Kuesto" style={{ width: 100 }} />
        </div>

        <KycProgressBar currentStep={6} totalSteps={10} />

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: UI.spacing.xl }}>
          <h2 style={{ color: SEMANTIC.actionBuy, fontSize: UI.fontSize.title, fontWeight: UI.fontWeight.bold, marginBottom: UI.spacing.sm }}>
            {t('kyc.payment.title', 'Metodo di pagamento')}
          </h2>
          <p style={{ color: SEMANTIC.textSecondary, fontSize: UI.fontSize.sm, margin: 0 }}>
            {t('kyc.payment.subtitle', 'I tuoi dati sono protetti e cifrati')}
          </p>
        </div>

        {/* Mockup banner */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          backgroundColor: 'rgba(255,170,0,0.10)', border: '1px solid rgba(255,170,0,0.35)',
          borderRadius: UI.borderRadius.sm, padding: '8px 14px', marginBottom: UI.spacing.lg,
        }}>
          <i className="bi bi-flask" style={{ color: '#ffaa00', fontSize: 14 }} />
          <span style={{ color: '#ffaa00', fontSize: 12, fontWeight: 600 }}>
            {t('kyc.payment.mock_notice', 'MODALITÀ SIMULAZIONE — nessun addebito reale verrà effettuato')}
          </span>
        </div>

        {/* Plan summary */}
        {plan && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 14,
            backgroundColor: SEMANTIC.backgroundCard,
            border: `1px solid ${planColor}44`,
            borderRadius: UI.borderRadius.md, padding: '14px 18px', marginBottom: UI.spacing.xl,
          }}>
            <img src={plan.img} alt={plan.name} style={{ width: 44, height: 44, borderRadius: '50%' }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: planColor, fontWeight: UI.fontWeight.bold, fontSize: 16 }}>Piano {plan.name}</div>
              <div style={{ color: SEMANTIC.textSecondary, fontSize: 12, marginTop: 2 }}>
                Success fee {plan.pricing.value} sui guadagni
                {plan.pricing.sub && <span style={{ color: planColor }}> {plan.pricing.sub}</span>}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: planColor, fontWeight: UI.fontWeight.bold, fontSize: 18 }}>
                {plan.pricing.sub ? plan.pricing.sub.replace('+ ', '') : 'Gratis'}
              </div>
              <div style={{ color: SEMANTIC.textSecondary, fontSize: 10 }}>al mese</div>
            </div>
          </div>
        )}

        {/* ── Visual card ── */}
        <div style={{
          background: cardBg,
          borderRadius: 16, padding: '24px 26px', marginBottom: UI.spacing.xl,
          boxShadow: '0 12px 40px rgba(0,0,0,0.45)', position: 'relative', overflow: 'hidden',
          aspectRatio: '1.586',
        }}>
          {/* Card background shimmer */}
          <div style={{
            position: 'absolute', top: -40, right: -40, width: 180, height: 180,
            borderRadius: '50%', background: 'rgba(255,255,255,0.04)',
          }} />
          <div style={{
            position: 'absolute', bottom: -50, left: -30, width: 200, height: 200,
            borderRadius: '50%', background: 'rgba(255,255,255,0.03)',
          }} />

          {/* Top row: chip + brand */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
            {/* Chip */}
            <div style={{
              width: 38, height: 28, borderRadius: 5,
              background: 'linear-gradient(135deg, #d4a843, #f0d070, #c8952a)',
              boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 22, height: 16, border: '1.5px solid rgba(0,0,0,0.2)', borderRadius: 3,
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, padding: 2 }}>
                {[0,1,2,3].map(i => <div key={i} style={{ background: 'rgba(0,0,0,0.18)', borderRadius: 1 }} />)}
              </div>
            </div>
            {/* Brand logo */}
            <div style={{ height: 28, display: 'flex', alignItems: 'center' }}>
              {brand === 'visa'       ? <VisaLogo />       :
               brand === 'mastercard' ? <MastercardLogo /> :
               <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>VISA / MC</span>}
            </div>
          </div>

          {/* Card number */}
          <div style={{
            color: 'white', fontSize: 18, letterSpacing: 3, fontFamily: 'monospace',
            marginBottom: 22, textShadow: '0 1px 3px rgba(0,0,0,0.5)',
          }}>
            {cvvFocus ? '•••• •••• •••• ••••' : maskCard(card)}
          </div>

          {/* Bottom row: name + expiry */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
            <div>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Titolare</div>
              <div style={{ color: 'white', fontSize: 13, letterSpacing: 1, fontFamily: 'monospace', textTransform: 'uppercase' }}>
                {name.trim() || 'NOME COGNOME'}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 9, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>Scade</div>
              <div style={{ color: 'white', fontSize: 13, fontFamily: 'monospace' }}>
                {expiry || 'MM/AA'}
              </div>
            </div>
          </div>

          {/* CVV overlay */}
          {cvvFocus && (
            <div style={{
              position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: 16,
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 11, marginBottom: 6 }}>CVV</div>
                <div style={{ color: 'white', fontSize: 22, letterSpacing: 6, fontFamily: 'monospace' }}>
                  {'•'.repeat(cvv.length || 3)}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Form ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: UI.spacing.xl }}>

          {/* Cardholder name */}
          <div>
            <label style={labelStyle}>{t('kyc.payment.name', 'Nome sul carta')}</label>
            <input
              placeholder="Mario Rossi"
              value={name}
              onChange={e => setName(e.target.value)}
              style={inputStyle}
              autoComplete="cc-name"
            />
          </div>

          {/* Card number */}
          <div>
            <label style={labelStyle}>{t('kyc.payment.card_number', 'Numero carta')}</label>
            <div style={{ position: 'relative' }}>
              <input
                ref={cardRef}
                placeholder="1234 5678 9012 3456"
                value={card}
                onChange={handleCardChange}
                style={{ ...inputStyle, paddingRight: 52 }}
                inputMode="numeric"
                autoComplete="cc-number"
              />
              {brand && (
                <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>
                  {brand === 'visa' ? <VisaLogo size={32} /> : <MastercardLogo size={34} />}
                </div>
              )}
            </div>
            {card.length > 0 && !brand && (
              <div style={{ color: '#ff8800', fontSize: 11, marginTop: 4 }}>
                Solo Visa e Mastercard accettate
              </div>
            )}
          </div>

          {/* Expiry + CVV row */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={labelStyle}>{t('kyc.payment.expiry', 'Scadenza')}</label>
              <input
                ref={expiryRef}
                placeholder="MM/AA"
                value={expiry}
                onChange={handleExpiryChange}
                onBlur={handleExpiryBlur}
                style={{ ...inputStyle, borderColor: expiryError ? '#ff4d4d' : 'rgba(255,255,255,0.15)' }}
                inputMode="numeric"
                autoComplete="cc-exp"
              />
              {expiryError && (
                <div style={{ color: '#ff4d4d', fontSize: 11, marginTop: 4 }}>{expiryError}</div>
              )}
            </div>
            <div>
              <label style={labelStyle}>CVV</label>
              <input
                ref={cvvRef}
                placeholder="•••"
                value={cvv}
                onChange={handleCvvChange}
                onFocus={() => setCvvFocus(true)}
                onBlur={() => setCvvFocus(false)}
                style={inputStyle}
                inputMode="numeric"
                autoComplete="cc-csc"
                type="password"
              />
            </div>
          </div>
        </div>

        {/* Security badges */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: UI.spacing.lg }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: SEMANTIC.textSecondary, fontSize: 11 }}>
            <i className="bi bi-lock-fill" style={{ color: SEMANTIC.actionBuy, fontSize: 12 }} />
            Connessione sicura SSL
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, color: SEMANTIC.textSecondary, fontSize: 11 }}>
            <i className="bi bi-shield-check" style={{ color: SEMANTIC.actionBuy, fontSize: 12 }} />
            PCI DSS compliant
          </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ backgroundColor: 'rgba(255,77,77,0.1)', border: `1px solid ${SEMANTIC.actionSell}`, borderRadius: UI.borderRadius.sm, padding: UI.spacing.sm, marginBottom: UI.spacing.md, color: SEMANTIC.actionSell, textAlign: 'center', fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={!isValid || saving}
          style={{
            width: '100%', padding: UI.spacing.md,
            backgroundColor: (!isValid || saving) ? SEMANTIC.backgroundDisabled : SEMANTIC.ctaPrimary,
            color: (!isValid || saving) ? SEMANTIC.textDisabled : SEMANTIC.ctaPrimaryText,
            border: 'none', borderRadius: UI.borderRadius.sm,
            fontSize: UI.fontSize.lg, fontWeight: UI.fontWeight.bold,
            cursor: (!isValid || saving) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {saving
            ? <><i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} /> Conferma...</>
            : <><i className="bi bi-lock-fill" /> {t('kyc.payment.confirm', 'Attiva piano')}</>}
        </button>

        {/* Stripe attribution (mockup) */}
        <div style={{ textAlign: 'center', marginTop: 14, color: SEMANTIC.textSecondary, fontSize: 11, opacity: 0.6 }}>
          Powered by <strong>Stripe</strong> (simulato)
        </div>

      </div>
    </div>
  );
};

const labelStyle = { color: SEMANTIC.textSecondary, fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 };
const inputStyle = {
  width: '100%', padding: '12px 14px', boxSizing: 'border-box',
  backgroundColor: SEMANTIC.backgroundCard, border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: UI.borderRadius.sm, color: SEMANTIC.textPrimary, fontSize: 15,
  outline: 'none', fontFamily: 'monospace', letterSpacing: 1,
};

export default PaymentSetup;
